import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const ROICalculatorSection = () => {
  const [formData, setFormData] = useState({
    companySize: '',
    deviceCategories: '',
    policyTeamSize: '',
    annualRevenue: ''
  });

  const [results, setResults] = useState(null);

  const companySizeOptions = [
    { value: 'small', label: 'Small (< $100M revenue)' },
    { value: 'medium', label: 'Medium ($100M - $1B revenue)' },
    { value: 'large', label: 'Large ($1B - $5B revenue)' },
    { value: 'enterprise', label: 'Enterprise (> $5B revenue)' }
  ];

  const deviceCategoryOptions = [
    { value: '1-2', label: '1-2 Categories' },
    { value: '3-5', label: '3-5 Categories' },
    { value: '6-10', label: '6-10 Categories' },
    { value: '10+', label: '10+ Categories' }
  ];

  const teamSizeOptions = [
    { value: '1-3', label: '1-3 People' },
    { value: '4-10', label: '4-10 People' },
    { value: '11-25', label: '11-25 People' },
    { value: '25+', label: '25+ People' }
  ];

  const calculateROI = () => {
    const baseValues = {
      small: { timeSaved: 520, riskReduction: 250000, efficiency: 35 },
      medium: { timeSaved: 1040, riskReduction: 750000, efficiency: 45 },
      large: { timeSaved: 2080, riskReduction: 2000000, efficiency: 55 },
      enterprise: { timeSaved: 3120, riskReduction: 5000000, efficiency: 65 }
    };

    const multipliers = {
      deviceCategories: { '1-2': 1, '3-5': 1.5, '6-10': 2, '10+': 2.5 },
      teamSize: { '1-3': 1, '4-10': 1.3, '11-25': 1.6, '25+': 2 }
    };

    const base = baseValues?.[formData?.companySize] || baseValues?.medium;
    const categoryMultiplier = multipliers?.deviceCategories?.[formData?.deviceCategories] || 1;
    const teamMultiplier = multipliers?.teamSize?.[formData?.policyTeamSize] || 1;

    const timeSaved = Math.round(base?.timeSaved * categoryMultiplier * teamMultiplier);
    const riskReduction = Math.round(base?.riskReduction * categoryMultiplier);
    const efficiency = Math.min(base?.efficiency * categoryMultiplier, 85);
    const annualSavings = Math.round((timeSaved * 75) + (riskReduction * 0.1));

    setResults({
      timeSaved,
      riskReduction,
      efficiency,
      annualSavings,
      roi: Math.round((annualSavings / 150000) * 100)
    });
  };

  const handleCalculate = (e) => {
    e?.preventDefault();
    if (formData?.companySize && formData?.deviceCategories && formData?.policyTeamSize) {
      calculateROI();
    }
  };

  return (
    <section className="bg-white py-12 md:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#10B981]/10 border border-[#10B981]/20 rounded-full px-4 py-2 mb-4">
            <Icon name="Calculator" size={16} color="#10B981" />
            <span className="text-xs md:text-sm font-medium text-[#10B981]">ROI Calculator</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1B1B20] mb-4">
            Calculate Your Enterprise ROI
          </h2>
          <p className="text-base md:text-lg text-[#6B7280] max-w-3xl mx-auto">
            See how PolicyAlerts delivers measurable value through time savings, risk reduction, and operational efficiency
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="bg-[#FAFBFC] rounded-2xl p-6 md:p-8">
            <h3 className="text-xl md:text-2xl font-bold text-[#1B1B20] mb-6">Your Organization</h3>
            
            <form onSubmit={handleCalculate} className="space-y-6">
              <Select
                label="Company Size"
                placeholder="Select company size"
                options={companySizeOptions}
                value={formData?.companySize}
                onChange={(value) => setFormData({ ...formData, companySize: value })}
                required
              />

              <Select
                label="Device Categories Monitored"
                placeholder="Select number of categories"
                options={deviceCategoryOptions}
                value={formData?.deviceCategories}
                onChange={(value) => setFormData({ ...formData, deviceCategories: value })}
                required
              />

              <Select
                label="Policy Monitoring Team Size"
                placeholder="Select team size"
                options={teamSizeOptions}
                value={formData?.policyTeamSize}
                onChange={(value) => setFormData({ ...formData, policyTeamSize: value })}
                required
              />

              <Input
                label="Estimated Annual Revenue (Optional)"
                type="text"
                placeholder="e.g., $500M"
                value={formData?.annualRevenue}
                onChange={(e) => setFormData({ ...formData, annualRevenue: e?.target?.value })}
              />

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                iconName="TrendingUp"
                iconPosition="left"
                className="bg-[#1A73E8] hover:bg-[#1A73E8]/90"
              >
                Calculate ROI
              </Button>
            </form>
          </div>

          <div className="bg-gradient-to-br from-[#1A73E8] to-[#0066FF] rounded-2xl p-6 md:p-8 text-white">
            <h3 className="text-xl md:text-2xl font-bold mb-6">Projected Annual Impact</h3>
            
            {results ? (
              <div className="space-y-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-90">Time Saved</span>
                    <Icon name="Clock" size={20} />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold">{results?.timeSaved?.toLocaleString()}</div>
                  <div className="text-sm opacity-75 mt-1">hours annually</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-90">Risk Reduction Value</span>
                    <Icon name="ShieldCheck" size={20} />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold">${(results?.riskReduction / 1000000)?.toFixed(1)}M</div>
                  <div className="text-sm opacity-75 mt-1">in avoided policy monitoring issues</div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-90">Efficiency Improvement</span>
                    <Icon name="Zap" size={20} />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold">{results?.efficiency}%</div>
                  <div className="text-sm opacity-75 mt-1">operational efficiency gain</div>
                </div>

                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border-2 border-white/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">Total Annual Savings</span>
                    <Icon name="DollarSign" size={20} />
                  </div>
                  <div className="text-4xl md:text-5xl font-bold">${(results?.annualSavings / 1000000)?.toFixed(2)}M</div>
                  <div className="text-sm opacity-90 mt-2">ROI: {results?.roi}% in first year</div>
                </div>

                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  iconName="Download"
                  iconPosition="left"
                  className="border-white/30 text-white hover:bg-white/10"
                >
                  Download Full ROI Report
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-white/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name="BarChart3" size={32} color="#FFFFFF" />
                </div>
                <p className="text-lg opacity-90 mb-2">Complete the form to see your results</p>
                <p className="text-sm opacity-75">Get instant insights into your potential ROI</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 md:mt-12 bg-[#F8F9FA] rounded-xl p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#1A73E8]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Info" size={20} color="#1A73E8" />
            </div>
            <div>
              <h4 className="text-base md:text-lg font-semibold text-[#1B1B20] mb-2">How We Calculate ROI</h4>
              <p className="text-sm md:text-base text-[#6B7280] leading-relaxed">
                Our ROI calculator uses industry benchmarks and 13 years of client data to estimate time savings from automated policy monitoring, risk reduction from early policy change detection, and efficiency gains from centralized policy intelligence. Actual results vary based on implementation scope and organizational factors. Schedule a consultation for a customized ROI analysis.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculatorSection;